import {
  addDays as df_addDays,
  addHours as df_addHours,
  addMonths as df_addMonths,
  addSeconds as df_addSeconds,
  addWeeks as df_addWeeks,
  differenceInCalendarDays as df_differenceInCalendarDays,
  endOfDay as df_endOfDay,
  endOfMonth as df_endOfMonth,
  endOfWeek as df_endOfWeek,
  format as df_format,
  getDate as df_getDate,
  getDay as df_getDay,
  getHours as df_getHours,
  getMinutes as df_getMinutes,
  getMonth as df_getMonth,
  getYear as df_getYear,
  isAfter as df_isAfter,
  isBefore as df_isBefore,
  isEqual as df_isEqual,
  isSameDay as df_isSameDay,
  isSameMinute as df_isSameMinute,
  isWithinRange as df_isWithinRange,
  parse as df_parse,
  setDate as df_setDate,
  setHours as df_setHours,
  setMinutes as df_setMinutes,
  setMonth as df_setMonth,
  setYear as df_setYear,
  startOfDay as df_startOfDay,
  startOfMonth as df_startOfMonth,
  startOfWeek as df_startOfWeek,
} from "date-fns";

import { IEvent } from "../classes/event";
import { IText_Days } from "../classes/Text";

export const parse = (strDate: string): Date => {
  return df_parse(strDate);
};

export const parseDate = (dt: Date): Date => {
  return df_parse(dt);
};

export const setCurrentYear = (currentDate: Date, year: number): Date => {
  // Sets to year passed in, same month as current date, on the first of that month.
  return new Date(year, currentDate.getMonth(), 1);
};

export const setCurrentMonth = (currentDate: Date, month: number): Date => {
  // Sets to same year as current date, month as passed in, on the first of that month.
  return new Date(currentDate.getFullYear(), month, 1);
};

export const getFirstDateInCurrentMonth_Mobile = (currentDate: Date): Date => {
  return df_startOfMonth(currentDate);
};

export const getLastDateInCurrentMonth_Mobile = (currentDate: Date): Date => {
  return df_endOfMonth(currentDate);
};

export const getFirstDateInCurrentMonth = (currentDate: Date, firstDayOfWeek: string, includeLastDaysOfPreviousMonth: boolean): Date => {
  // Note: This includes the days from the previous month that are shown in the first week of the month grid view.

  let numberDaysToShowFromLastMonth = 0;

  const firstDateOfMonth = df_startOfMonth(currentDate);

  if (includeLastDaysOfPreviousMonth) {

    const dayNumberOfFirstDayOfMonth = df_getDay(firstDateOfMonth);
    if (firstDayOfWeek.toUpperCase() === "MONDAY") {
      switch (dayNumberOfFirstDayOfMonth) {
        case 1: numberDaysToShowFromLastMonth = 0; break;
        case 2: numberDaysToShowFromLastMonth = 1; break;
        case 3: numberDaysToShowFromLastMonth = 2; break;
        case 4: numberDaysToShowFromLastMonth = 3; break;
        case 5: numberDaysToShowFromLastMonth = 4; break;
        case 6: numberDaysToShowFromLastMonth = 5; break;
        case 0: numberDaysToShowFromLastMonth = 6; break;
        default: numberDaysToShowFromLastMonth = 0; break;
      }
    } else { // Week starts on Sunday.
      switch (dayNumberOfFirstDayOfMonth) {
        case 0: numberDaysToShowFromLastMonth = 0; break;
        case 1: numberDaysToShowFromLastMonth = 1; break;
        case 2: numberDaysToShowFromLastMonth = 2; break;
        case 3: numberDaysToShowFromLastMonth = 3; break;
        case 4: numberDaysToShowFromLastMonth = 4; break;
        case 5: numberDaysToShowFromLastMonth = 5; break;
        case 6: numberDaysToShowFromLastMonth = 6; break;
        default: numberDaysToShowFromLastMonth = 0; break;
      }
    }

  }

  const firstDisplayedDate = df_addDays(firstDateOfMonth, numberDaysToShowFromLastMonth * -1);

  return firstDisplayedDate;
};

export const getLastDateInCurrentMonth = (currentDate: Date, firstDayOfWeek: string, includeFirstDaysOfNextMonth: boolean): Date => {
  // Note: This includes the days from the next month that are shown in the last week of the month grid view.

  let numberDaysToShowFromNextMonth = 0;

  const lastDateOfMonth = df_endOfMonth(currentDate);

  if (includeFirstDaysOfNextMonth) {

    const dayNumberOfLastDayOfMonth = df_getDay(lastDateOfMonth);
    if (firstDayOfWeek.toUpperCase() === "MONDAY") {
      switch (dayNumberOfLastDayOfMonth) {
        case 1: numberDaysToShowFromNextMonth = 6; break;
        case 2: numberDaysToShowFromNextMonth = 5; break;
        case 3: numberDaysToShowFromNextMonth = 4; break;
        case 4: numberDaysToShowFromNextMonth = 3; break;
        case 5: numberDaysToShowFromNextMonth = 2; break;
        case 6: numberDaysToShowFromNextMonth = 1; break;
        case 0: numberDaysToShowFromNextMonth = 0; break;
        default: numberDaysToShowFromNextMonth = 0; break;
      }
    } else { // Week starts on Sunday.
      switch (dayNumberOfLastDayOfMonth) {
        case 0: numberDaysToShowFromNextMonth = 6; break;
        case 1: numberDaysToShowFromNextMonth = 5; break;
        case 2: numberDaysToShowFromNextMonth = 4; break;
        case 3: numberDaysToShowFromNextMonth = 3; break;
        case 4: numberDaysToShowFromNextMonth = 2; break;
        case 5: numberDaysToShowFromNextMonth = 1; break;
        case 6: numberDaysToShowFromNextMonth = 0; break;
        default: numberDaysToShowFromNextMonth = 0; break;
      }
    }

  }

  const lastDisplayedDate = df_addDays(lastDateOfMonth, numberDaysToShowFromNextMonth);

  return lastDisplayedDate;
};

export const getFirstDateInCurrentWeek = (currentDate: Date, firstDayOfWeek: string): Date => {
  const indexOfFirstDayOfWeek = getDayNumberByName(firstDayOfWeek);
  return df_startOfWeek(currentDate, { weekStartsOn: indexOfFirstDayOfWeek });
};

export const getLastDateInCurrentWeek = (currentDate: Date, firstDayOfWeek): Date => {
  const indexOfFirstDayOfWeek = getDayNumberByName(firstDayOfWeek);
  return df_endOfWeek(currentDate, { weekStartsOn: indexOfFirstDayOfWeek });
};

export const getFirstDateInCurrentDay = (currentDate: Date): Date => {
  return df_startOfDay(currentDate);
};

export const getLastDateInCurrentDay = (currentDate: Date): Date => {
  return df_endOfDay(currentDate);
};

export const getFirstDateInFourMonths = (currentDate: Date): Date => {
  return df_startOfMonth(currentDate);
};

export const getLastDateInFourMonths = (currentDate: Date): Date => {
  return df_endOfMonth(df_addMonths(currentDate, 3));
};

export const changeTimePeriod = (currentDate: Date, viewMode: string, periodsShifted: number, firstDayOfWeek: string): Date => {
  switch (viewMode) {
    case "DAILYLIST":
    case "DAILY": {
      return df_addDays(currentDate, periodsShifted);
    }
    case "WEEKLY": {
      const indexOfFirstDayOfWeek = getDayNumberByName(firstDayOfWeek);
      const options = { weekStartsOn: indexOfFirstDayOfWeek };
      return df_startOfWeek(df_addWeeks(currentDate, periodsShifted), options);
    }
    case "MONTHLY":
    case "MONTHLYLIST": {
      return df_startOfMonth(df_addMonths(currentDate, periodsShifted));
    }
    default: {
      throw Error("Unknown viewMode");
    }
  }
};

export const getDayNumber = (date: Date): number => {
  return df_getDay(date);
};

export const getDayOfMonth = (date: Date): number => {
  return df_getDate(date);
};

export const getMonthNumber = (date: Date): number => {
  return df_getMonth(date);
};

export const getDayNumberByName = (dayName: string): 0 | 1 | 2 | 3 | 4 | 5 | 6 => {
  switch (dayName.toUpperCase()) {
    case "SUNDAY": return 0;
    case "MONDAY": return 1;
    case "TUESDAY": return 2;
    case "WEDNESDAY": return 3;
    case "THURSDAY": return 4;
    case "FRIDAY": return 5;
    case "SATURDAY": return 6;
    default: return 0;
  }
};

export const addDays = (date: Date, days: number): Date => {
  return df_addDays(date, days);
};

export const addHours = (date: Date, hours: number): Date => {
  return df_addHours(date, hours);
};

export const addSeconds = (date: Date, seconds: number): Date => {
  return df_addSeconds(date, seconds);
};

export const getDaysBetweenDates = (date1: Date, date2: Date): number => {
  return df_differenceInCalendarDays(df_startOfDay(date2), df_endOfDay(date1));
};

// Careful - this just gives the difference in the month numbers of two dates (regardless of year), not the number of months between two dates.
export const getMonthsBetweenDates = (date1: Date, date2: Date): number => {
  return df_getMonth(date1) - df_getMonth(date2);
};

export const isBetween = (date1: Date, startDate: Date, endDate: Date): boolean => {
  return df_isWithinRange(date1, df_startOfDay(startDate), df_endOfDay(endDate));
};

// Only use the following format...() methods to format dates that are NOT for events, such as current date or the span of dates currently
// being displayed.

export const formatMonthDay = (date1: Date): string => {
  return df_format(date1, "MMMM D");
};

export const formatMonthName = (date1: Date): string => {
  return df_format(date1, "MMMM");
};

export const formatAbbreviatedMonthName = (date1: Date): string => {
  return df_format(date1, "MMM");
};

export const formatYearMonthDay = (date1: Date): string => {
  return df_format(date1, "YYYY-MM-DD");
};

export const formatYearMonthDayNoSeperator = (date1: Date): string => {
  return df_format(date1, "YYYYMMDD");
};

export const formatDaynameMonthYear = (date1: Date): string => {
  return df_format(date1, "dddd, MMMM D, YYYY");
};

export const formatYYYYMMDDHHMMSS = (date1: Date): string => {
  return df_format(date1, "YYYY-MM-DD HH:mm:ss");
};

// Only use format() for formatting the current date... avoid using on event dates as JS may convert the date to local timezone or daylight savings.
// Wherever possible, format event dates on the server and pass the resulting string back on the service response; i.e. event.preformatted_localStartDate.
export const format = (date1: Date, dateFormat: string, includeTime: boolean = false, userTimeFormat?: string): string => {
  let finalFormat = "";
  switch (dateFormat) {
    case "M/d/yyyy": finalFormat = "M/D/YYYY"; break;            // 2/28/2008
    case "M/d/yy": finalFormat = "M/D/YY"; break;                // 2/28/08
    case "MM/dd/yy": finalFormat = "MM/DD/YY"; break;            // 02/28/08
    case "MM/dd/yyyy": finalFormat = "MM/DD/YYYY"; break;        // 02/28/2008
    case "MM/DD/YYYY": finalFormat = "MM/DD/YYYY"; break;        // 02/28/2008
    case "yy/MM/dd": finalFormat = "YY/MM/DD"; break;            // 08/02/28
    case "yyyy-MM-dd": finalFormat = "YYYY-MM-DD"; break;        // 2008-02-28
    case "dd-MMMM-yy": finalFormat = "DD-MMMM-YY"; break;        // 28-February-08
    case "dd-MMM-yy": finalFormat = "DD-MMM-YY"; break;          // 28-Feb-08
    case "dd MMMM, yyyy": finalFormat = "DD MMMM, YYYY"; break;  // 28 February, 2008
    case "MMMM dd, yyyy": finalFormat = "MMMM DD, YYYY"; break;  // February 28, 2008
    case "ddd, MMM dd, yyyy": finalFormat = "ddd, MMM DD, YYYY"; break;    // Thu, Feb 28, 2008
    case "dddd,dd MMMM, yyyy": finalFormat = "dddd,DD MMMM, YYYY"; break;  // Thursday,28 February, 2008
    case "dddd, MMMM dd, yyyy": finalFormat = "dddd, MMMM DD, YYYY"; break;         // Thursday, February 28, 2008
    case "MMMM Do yyyy": finalFormat = "MMMM Do YYYY"; break;  // February 28th 2008
    case "NODATE": finalFormat = ""; break;                      // Return blank (used when only showing time, not date)
    default: finalFormat = "YYYY-MM-DD";                         // Default format for blank/unrecognised time format.
  }

  if (includeTime) {
    if (finalFormat.length > 0) { finalFormat += " "; }
    if (userTimeFormat) {
      finalFormat += userTimeFormat;
    } else {
      finalFormat += "h:mmA";
    }
  }

  const formatedDateStr = df_format(date1, finalFormat);
  return formatedDateStr;
};

export const getHours = (date1: Date): number => {
  return df_getHours(date1);
};

export const getMinutes = (date1: Date): number => {
  return df_getMinutes(date1);
};

export const isBefore = (date1: Date, date2: Date): boolean => {
  return df_isBefore(date1, date2);
};

export const isAfter = (date1: Date, date2: Date): boolean => {
  return df_isAfter(date1, date2);
};

export const isEqual = (date1: Date, date2: Date): boolean => {
  return df_isEqual(date1, date2);
};

export const isSameDayOrBefore = (date1: Date, date2: Date): boolean => {
  const isSameDate = isSameDay(date1, date2);
  const isBeforeDate = isBefore(date1, date2);
  return isSameDate || isBeforeDate;
};

export const isSameDayOrAfter = (date1: Date, date2: Date): boolean => {
  const isSameDate = isSameDay(date1, date2);
  const isAfterDate = isAfter(date1, date2);
  return isSameDate || isAfterDate;
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return df_isSameDay(date1, date2);
};

export const eventDescription = (event: IEvent, allDayText: string) => {

  if (event.isAllDay) {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      return allDayText; // All Day, one-day event. Just show 'All Day'.
    } else {
      return event.preformatted_localStartDate + " - " + event.preformatted_localEndDate; // All Day, multiple day event. Show 'Date - Date'.
    }
  } else {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      return event.preformatted_localStartTime + " - " + event.preformatted_localEndTime; // All Day, one-day event. Show 'Time - Time'.
    } else {
      return event.preformatted_localStartDate + " " + event.preformatted_localStartTime + " - " + event.preformatted_localEndDate + " " + event.preformatted_localEndTime; // All Day, multiple day event. Show 'Date Time - Date Time'.
    }
  }

};

export const eventDescription_DailyList = (event: IEvent, allDayText: string, selectedDate: Date) => {
  // Daily list only ever displays the start time and end time for the current day - never the date part.
  let result = "";
  if (event.isAllDay) {
    result = allDayText; // All Day, one-day event. Just show 'All Day'.
  } else {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      // Event that takes place on a single day; show the start and end times, unless runs for entire day, then show All Day.
      result = event.preformatted_localStartTime + " - " + event.preformatted_localEndTime; // Show 'Time - Time' or 'All Day'.
    } else {
      // Event that takes place over several days.
      // If event started before current day, display start time as midnight (12:00am).
      // If event ends after current day, display end time as end of the day (11:59pm).
      let startTime = event.preformatted_localStartTime;
      if (isBefore(event.localStartDate, df_startOfDay(selectedDate))) { startTime = "12:00AM"; }

      let endTime = event.preformatted_localEndTime;
      if (isAfter(event.localEndDate, df_endOfDay(selectedDate))) { endTime = "11:59PM"; }

      result = startTime + " - " + endTime;
    }
  }

  // If event runs all day (12:00am to 11:59pm), display as All Day (not with start time to end time).
  if (result === "12:00AM - 11:59PM") { result = allDayText; }

  return result;
};

export const eventDescription_DailyList_ScreenReader = (event: IEvent, allDayText: string, selectedDate: Date, eventText: string, toText: string, EventName: string = "") => {
  // Daily list only ever displays the start time and end time for the current day - never the date part.
  let result = "";
  const dateFormat = "dddd MMMM Do YYYY";
  if (EventName.trim() !== "") { result += eventText + " " + EventName + ", on "; }
  if (event.isAllDay) {
    result += df_format(selectedDate, dateFormat) + " " + allDayText;
  } else {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      // Event that takes place on a single day; show the start and end times, unless runs for entire day, then show All Day.
      result += df_format(selectedDate, dateFormat) + " ";
      if (event.preformatted_localStartTime === "12:00AM" && event.preformatted_localEndTime === "11:59PM") {
        result += allDayText; // Show 'Add Day'.
      } else {
        result += event.preformatted_localStartTime + " " + toText + " " + event.preformatted_localEndTime; // Show 'Time - Time'.
      }
    } else {
      // Event that takes place over several days.
      // If event started before current day, display start time as midnight (12:00am).
      // If event ends after current day, display end time as end of the day (11:59pm).
      // If event runs all day (12:00am to 11:59pm), display as All Day (not with start time to end time).
      let startTime = event.preformatted_localStartTime;
      if (isBefore(event.localStartDate, df_startOfDay(selectedDate))) { startTime = "12:00AM"; }

      let endTime = event.preformatted_localEndTime;
      if (isAfter(event.localEndDate, df_endOfDay(selectedDate))) { endTime = "11:59PM"; }

      result += df_format(selectedDate, dateFormat) + " ";

      if (startTime === "12:00AM" && endTime === "11:59PM") {
        result += allDayText;
      } else {
        result += startTime + " " + toText + " " + endTime;
      }

    }
  }
  return result;
};

export const eventDetailDescription = (event: IEvent, allDayText: string) => {

  if (event.isAllDay) {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      return event.preformatted_localStartDate + " - " + allDayText; // All Day, one-day event. Just show 'All Day'.
    } else {
      return event.preformatted_localStartDate + " - " + event.preformatted_localEndDate + " - " + allDayText; // All Day, multiple day event. Show 'Date - Date'.
    }
  } else {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      return event.preformatted_localStartDate + " " + event.preformatted_localStartTime + " - " + event.preformatted_localEndTime; // All Day, one-day event. Show 'Time - Time'.
    } else {
      return event.preformatted_localStartDate + " " + event.preformatted_localStartTime + " - " + event.preformatted_localEndDate + " " + event.preformatted_localEndTime; // All Day, multiple day event. Show 'Date Time - Date Time'.
    }
  }

};

export const eventDescriptionMonthlyList = (event: IEvent, allDayText: string) => {

  if (event.isAllDay) {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      return event.preformatted_localEndDate + " " + allDayText; // All Day, one-day event. Just show 'All Day'.
    } else {
      return event.preformatted_localStartDate + " - " + event.preformatted_localEndDate + " " + allDayText; // All Day, multiple day event. Show 'Date - Date All Day'.
    }
  } else {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      return event.preformatted_localStartDate + " " + event.preformatted_localStartTime + " - " + event.preformatted_localEndTime; // All Day, one-day event. Show 'Time - Time'.

    } else {
      return event.preformatted_localStartDate + " " + event.preformatted_localStartTime + " - " + event.preformatted_localEndDate + " " + event.preformatted_localEndTime; // Non-All Day event. Show 'Date Time - Date Time'.
    }
  }

};

export const eventDescriptionMonthlyList_Screenreader = (event: IEvent, allDayText: string, onText: string, toText: string) => {

  let result = onText + " ";
  if (event.isAllDay) {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      result += event.preformatted_localStartDate_screenreader + " " + allDayText; // All Day, one-day event. Just show 'All Day'.
    } else {
      result += event.preformatted_localStartDate_screenreader + " " + allDayText + " " + toText + " " + event.preformatted_localEndDate_screenreader + " " + allDayText; // All Day, multiple day event. Show 'Date - Date All Day'.
    }
  } else {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      result += event.preformatted_localStartDate_screenreader + " " + event.preformatted_localStartTime + " " + toText + " " + event.preformatted_localEndTime; // All Day, one-day event. Show 'Time - Time'.

    } else {
      result += event.preformatted_localStartDate_screenreader + " " + event.preformatted_localStartTime + " " + toText + " " + event.preformatted_localEndDate_screenreader + " " + event.preformatted_localEndTime; // Non-All Day event. Show 'Date Time - Date Time'.
    }
  }
  return result;

};

export const eventDescriptionMobile_Preformatted = (event: IEvent, allDayText: string, selectedDate: Date) => {
  let result = "";
  if (event.isAllDay) {
    result += allDayText;
  } else {
    if (getDaysBetweenDates(event.localStartDate, event.localEndDate) === 0) {
      // Event that takes place on a single day; show the start and end times (or All Day if runs from midnight to midnight)
      if (event.preformatted_localStartTime === "12:00AM" && event.preformatted_localEndTime === "11:59PM") {
        result += allDayText; // Show 'Add Day'.
      } else {
        result += event.preformatted_localStartTime + " to " + event.preformatted_localEndTime; // Show 'Time - Time'.
      }
    } else {
      // Event that takes place over several days.
      // If event started before current day, display start time as midnight.
      // If event ends after current day, display end time as end of the day (12:59pm).
      // If event runs all day (12:00AM to 11:59PM), display as All Day (not with start time to end time).
      let startTime = event.preformatted_localStartTime;
      if (isBefore(event.localStartDate, df_startOfDay(selectedDate))) { startTime = "12:00AM"; }

      let endTime = event.preformatted_localEndTime;
      if (isAfter(event.localEndDate, df_endOfDay(selectedDate))) { endTime = "11:59PM"; }

      if (startTime === "12:00AM" && endTime === "11:59PM") {
        result += allDayText;
      } else {
        result += startTime + " to " + endTime;
      }

    }
  }
  return result;
};

export const compareTimeOfDay = (date1: Date, date2: Date, name1: string, name2: string): number => {
  if (df_isSameMinute(date1, date2)) {
    return compareName(name1, name2);
  } else {
    if (isBefore(date1, date2)) { return -1; }
    return 1;
  }
};

export const compareName = (name1: string, name2: string): number => {
  if (name1 < name2) { return -1; }
  if (name1 === name2) { return 0; }
  return 1;
};

export const getInternationalisedDayName = (theDate: Date, daysText: IText_Days): string => {
  const dayNumberInWeek = getDayNumber(theDate);

  switch (dayNumberInWeek) {
    case 0: return daysText.sunday;
    case 1: return daysText.monday;
    case 2: return daysText.tuesday;
    case 3: return daysText.wednesday;
    case 4: return daysText.thursday;
    case 5: return daysText.friday;
    case 6: return daysText.saturday;
    default: return "?";
  }
};

export const copyHourAndMinute = (date1: Date, date2: Date): Date => {
  // Sets hour and minute from date1 to date2.
  let newDate = new Date(date2.valueOf());
  newDate = df_setHours(newDate, df_getHours(date1));
  newDate = df_setMinutes(newDate, df_getMinutes(date1));
  return newDate;
};

export const copyDayAndMonthAndYearFromDate1ToDate2 = (date1: Date, date2: Date): Date => {
  // Sets day, month, year, hour and minute from date1 to date2. Should preserve the time zone offset.
  let newDate = new Date(date2.valueOf());
  newDate = df_setDate(newDate, df_getDate(date1));
  newDate = df_setMonth(newDate, df_getMonth(date1));
  newDate = df_setYear(newDate, df_getYear(date1));
  return newDate;
};

export const setHourAndMinute = (date1: Date, hours: number, minutes: number): Date => {
  let newDate = new Date(date1.valueOf());
  newDate = df_setHours(newDate, hours);
  newDate = df_setMinutes(newDate, minutes);
  return newDate;
};
